import type { ExchangeRate } from '../../models/ExchangeRate';
import { AuditActionType } from '../../models/enums';
import { apiClient } from '../api/apiClient';
import { createId, delay, readCollection, seedCollection, writeCollection } from './dataverseClient';
import { auditLogService } from './auditLogService';
import { exchangeRatesSeed } from './mockData';

const ENTITY = 'cr_crm_exchangerate';

// Map Prisma DB row → frontend ExchangeRate model
function mapApiRate(r: Record<string, unknown>): ExchangeRate {
  return {
    cr_crm_exchangerateid: (r['id'] as string) ?? '',
    cr_crm_rate: Number(r['rate'] ?? 0),
    cr_crm_effectivefrom: (r['effectiveFrom'] as string) ?? '',
    cr_crm_effectiveto: r['effectiveTo'] as string | undefined,
    cr_crm_iscurrent: Boolean(r['isCurrent']),
    cr_crm_notes: r['notes'] as string | undefined,
    createdon: r['createdAt'] as string | undefined,
    ownerid: r['ownerId'] as string | undefined,
  };
}

class ExchangeRateService {
  private ensureSeed(): void {
    seedCollection(ENTITY, exchangeRatesSeed);
  }

  async list(): Promise<ExchangeRate[]> {
    if (apiClient.isConfigured()) {
      const res = await apiClient.get<Record<string, unknown>[] | { items: Record<string, unknown>[] }>('/api/exchange-rates');
      const raw = Array.isArray(res) ? res : (res.items ?? []);
      return raw.map(mapApiRate);
    }
    this.ensureSeed();
    await delay();
    return readCollection<ExchangeRate>(ENTITY).sort((a, b) => b.cr_crm_effectivefrom.localeCompare(a.cr_crm_effectivefrom));
  }

  async getCurrent(): Promise<ExchangeRate> {
    if (apiClient.isConfigured()) {
      const raw = await apiClient.get<Record<string, unknown>>('/api/exchange-rates/current');
      return mapApiRate(raw);
    }
    this.ensureSeed();
    await delay();
    return readCollection<ExchangeRate>(ENTITY).find((r) => r.cr_crm_iscurrent) ?? readCollection<ExchangeRate>(ENTITY)[0];
  }

  async createCurrent(rate: number, effectiveFrom: string, notes?: string): Promise<ExchangeRate> {
    if (apiClient.isConfigured()) {
      const raw = await apiClient.post<Record<string, unknown>>('/api/exchange-rates', { rate, effectiveFrom, notes });
      return mapApiRate(raw);
    }
    this.ensureSeed();
    await delay();
    const currentItems = readCollection<ExchangeRate>(ENTITY).map((r) => ({
      ...r,
      cr_crm_iscurrent: false,
      cr_crm_effectiveto: r.cr_crm_effectiveto ?? effectiveFrom,
    }));
    const next: ExchangeRate = {
      cr_crm_exchangerateid: createId('rate'),
      cr_crm_rate: Number(rate.toFixed(6)),
      cr_crm_effectivefrom: effectiveFrom,
      cr_crm_iscurrent: true,
      cr_crm_notes: notes,
      cr_crm_source: 'Manual update',
      createdon: new Date().toISOString(),
    };
    writeCollection(ENTITY, [next, ...currentItems]);
    await auditLogService.record({
      actionType: AuditActionType.Update,
      entityName: 'ExchangeRate',
      entityId: next.cr_crm_exchangerateid,
      description: `Exchange rate updated to ${next.cr_crm_rate}.`,
    });
    return next;
  }
}

export const exchangeRateService = new ExchangeRateService();
